import { BoundMultiSelectField, Css, useTestIds } from "@homebound/beam";
import { ObjectConfig, ObjectState } from "@homebound/form-state";
import { ArchivedTag } from "src/components/ArchivedTag";
import { LocationPathField } from "src/components/locationPathField/LocationPathField";
import { CostType, Maybe, SaveTakeoffLineItemInput } from "src/generated/graphqlTypes";
import {
  AddEditItemModal_LocationFragment,
  useAddEditItemModal_GetReadyPlanQuery,
} from "src/routes/libraries/plan-package/takeoffs/components/AddItems/AddEditItemModal.generated";
import { TakeoffTaskBoundSelectField } from "src/routes/libraries/plan-package/takeoffs/components/AddItems/utils";
import { BulkEditFields_TliFragment } from "src/routes/libraries/plan-package/takeoffs/components/BulkEditFields.generated";
import { SaveTliResult, TakeoffsStoreState } from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { pluralize } from "src/utils";
import { StoreApi } from "zustand";

type BulkEditFieldsProps = {
  store: StoreApi<TakeoffsStoreState>;
  formState: ObjectState<BulkEditObjectState>;
  tlis: BulkEditFields_TliFragment[];
};

/** The bulk edit fields, shared by the v1 modal and the V2 side panel. */
export function BulkEditFields(props: BulkEditFieldsProps) {
  const { store, formState, tlis } = props;
  const { planPackage } = store.getState();
  const tid = useTestIds(props, "bulkEdit");
  const { data: rpData } = useAddEditItemModal_GetReadyPlanQuery({
    variables: { readyPlanId: planPackage.id, versionId: planPackage.version.id },
  });

  const laborLines = tlis.filter((tli) => tli.costType === CostType.Labor);
  const linesWithoutLocationEdit = tlis.filter((tli) => !tli.canEditLocation.allowed);

  return (
    <>
      <LocationPathField
        filter={{ rpav: [planPackage.version.id] }}
        newLocationPathField={formState.newLocationPath}
        field={formState.locationId}
        onSelect={(_locationId, location) => formState.location.set(location)}
      />
      {linesWithoutLocationEdit.nonEmpty ? (
        <div css={Css.sm.red500.$} {...tid.locationWarning}>
          {linesWithoutLocationEdit.length} {pluralize(linesWithoutLocationEdit.length, "line")} selected won't be
          updated by the location field.
        </div>
      ) : null}
      <TakeoffTaskBoundSelectField taskId={formState.taskId} readOnly={false} />
      {laborLines.nonEmpty ? (
        <div css={Css.sm.red500.$} {...tid.taskWarning}>
          {laborLines.length} labor {pluralize(laborLines.length, "line is", "lines are")} selected,
          {pluralize(laborLines.length, " its task ", " their tasks ")}
          won't be updated by the task field.
        </div>
      ) : null}
      {/* This is copied from AddItems/utils.tsx common fields  but without UOM and Qty fields */}
      {/* At some point if we revisit canEditOptions, we can add this back in */}
      <BoundMultiSelectField
        label="Options"
        field={formState.optionIds}
        options={rpData?.readyPlan?.options ?? []}
        getOptionLabel={(o) => o.displayName + (o.active ? "" : " (Archived)")}
        getOptionValue={(o) => o.id}
        getOptionMenuLabel={(o) => <ArchivedTag active={o.active}>{o.displayName}</ArchivedTag>}
      />
    </>
  );
}

export type BulkEditObjectState = Omit<SaveTakeoffLineItemInput, "id"> & {
  location: Maybe<AddEditItemModal_LocationFragment>;
  newLocationPath?: Maybe<string>;
};

export const bulkEditFormConfig: ObjectConfig<BulkEditObjectState> = {
  locationId: { type: "value" },
  optionIds: { type: "value" },
  taskId: { type: "value" },

  // The fully-assembled path (i.e., parent path + new feature path) which only gets set when adding a new feature
  newLocationPath: { type: "value" },
  // Also store the more detailed objects for easier access/logic needs
  location: { type: "value" },
};

/** Applies only the changed fields to every line, skipping labor tasks and locked locations. */
export async function saveBulkEdit(params: {
  formState: ObjectState<BulkEditObjectState>;
  saveTli: (item: SaveTakeoffLineItemInput | SaveTakeoffLineItemInput[]) => SaveTliResult;
  tlis: BulkEditFields_TliFragment[];
}) {
  const { formState, saveTli, tlis } = params;

  const {
    newLocationPath,
    // Pulling out location here so that it does not end up in rest
    location: changedLocation,
    locationId,
    ...rest
  } = formState.changedValue;

  await saveTli(
    tlis.map((tli) => {
      return {
        id: tli.id,
        ...rest,
        taskId: tli.costType === CostType.Labor ? undefined : rest.taskId,
        ...(tli.canEditLocation.allowed
          ? newLocationPath
            ? { locationPath: newLocationPath }
            : locationId
              ? { locationId }
              : {}
          : {}),
      };
    }),
  );

  // Not `formState.update(...)`: this form sits above the individual lines and has no `init`, which `update` needs to
  // tell that the server acked the changes. Without the commit the form stays dirty after saving.
  formState.commitChanges();
}
